import { FC } from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from '../../../../helpers/hooks/useFormat';
import Button from '../../atoms/button';
import Dropdown, { DropdownProps, Option } from '../../atoms/dropdown';
import Review, { ReviewProps } from '../../molecules/review';
import { ReviewSortOption } from 'frontastic/tastics/products/details/types';

export interface ReviewListProps {
  isLoading?: boolean;
  headingLevel?: 'h4' | 'h5' | 'h6';
  loadMore?: () => void;
  reviews: ReviewProps[];
  sort?: (value: Option) => void;
  sortOptions: DropdownProps['selectOptions'];
  totalItems?: number;
  currentSortValue?: ReviewSortOption;
}

export const ReviewList: FC<ReviewListProps> = ({
  sortOptions,
  reviews,
  loadMore,
  totalItems = 0,
  headingLevel = 'h4',
  sort,
  isLoading = false,
  currentSortValue = 'most_recent',
}) => {
  const { formatMessage } = useFormat({ name: 'common' });

  return (
    <div>
      {/* Mobile View (default) */}
      <div className={'flex flex-col gap-10 space-y-2 md:hidden'}>
        <div className={'flex flex-row items-center justify-between gap-4'}>
          <Typography as={headingLevel} className={'body-text font-medium text-text-primary'}>
            {formatMessage({
              id: 'review.list.title',
              defaultMessage: 'Customer Reviews',
            })}
          </Typography>
          <Typography className={'body-text-small font-medium text-text-tertiary'}>
            {formatMessage({
              id: `${reviews.length ? 'review.list.count' : 'review.list.count.empty'}`,
              defaultMessage: `${reviews.length ? '1-{currentItems} of {totalItems} Reviews' : '0 Reviews'}`,
              values: {
                currentItems: reviews.length,
                totalItems: Math.max(reviews.length, totalItems),
              },
            })}
          </Typography>
        </div>
        {sort && sortOptions?.length && (
          <div className={'flex items-center gap-12'}>
            <Dropdown
              disabled={isLoading}
              selectOptions={sortOptions}
              label={formatMessage({
                id: 'review.list.sort.by',
                defaultMessage: 'Sort by:',
              })}
              selectOnChange={sort}
              selectedValue={currentSortValue}
              labelClassName={'whitespace-nowrap text-text-primary font-medium'}
              selectDropdownClassNames={'py-10 pl-12 pr-38'}
            />
          </div>
        )}
      </div>

      {/* Tablet View */}
      <div className={'hidden md:flex md:flex-row md:items-center md:justify-between lg:hidden'}>
        <Typography as={headingLevel} className={'body-text font-medium text-text-primary'}>
          {formatMessage({
            id: 'review.list.title',
            defaultMessage: 'Customer Reviews',
          })}
        </Typography>
        <div className={'flex items-center gap-10'}>
          <Typography className={'body-text-small font-medium text-text-tertiary'}>
            {formatMessage({
              id: `${reviews.length ? 'review.list.count' : 'review.list.count.empty'}`,
              defaultMessage: `${reviews.length ? '1-{currentItems} of {totalItems} Reviews' : '0 Reviews'}`,
              values: {
                currentItems: reviews.length,
                totalItems: Math.max(reviews.length, totalItems),
              },
            })}
          </Typography>
          {sort && sortOptions?.length && (
            <div className={'flex items-center gap-12'}>
              <Dropdown
                disabled={isLoading}
                selectOptions={sortOptions}
                label={formatMessage({
                  id: 'review.list.sort.by',
                  defaultMessage: 'Sort by:',
                })}
                selectOnChange={sort}
                selectedValue={currentSortValue}
                labelClassName={'whitespace-nowrap text-text-primary font-medium'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop View */}
      <div className={'hidden lg:flex lg:flex-col'}>
        <div className={'flex items-center justify-between'}>
          <Typography as={headingLevel} className={'body-text font-medium text-text-primary'}>
            {formatMessage({
              id: 'review.list.title',
              defaultMessage: 'Customer Reviews',
            })}
          </Typography>
          {sort && sortOptions?.length && (
            <div className={'flex items-center gap-12'}>
              <Dropdown
                disabled={isLoading}
                selectOptions={sortOptions}
                label={formatMessage({
                  id: 'review.list.sort.by',
                  defaultMessage: 'Sort by:',
                })}
                selectOnChange={sort}
                selectedValue={currentSortValue}
                labelClassName={'whitespace-nowrap text-text-primary font-medium'}
              />
            </div>
          )}
        </div>
        <Typography className={'body-text-small font-medium text-text-tertiary'}>
          {formatMessage({
            id: `${reviews.length ? 'review.list.count' : 'review.list.count.empty'}`,
            defaultMessage: `${reviews.length ? '1-{currentItems} of {totalItems} Reviews' : '0 Reviews'}`,
            values: {
              currentItems: reviews.length,
              totalItems: Math.max(reviews.length, totalItems),
            },
          })}
        </Typography>
      </div>

      <ul>
        {reviews.map((review) => (
          <li key={review.id} className={'border-t-2 border-stroke-primary py-24 first-of-type:border-none'}>
            <Review {...review} />
          </li>
        ))}
      </ul>

      {loadMore && (
        <div className={'pb-32 pt-24 md:w-206'}>
          <Button
            disabled={isLoading}
            size={'full'}
            type={'button'}
            variant="secondary"
            onClick={loadMore}
            iconPosition={'right'}
          >
            {formatMessage({
              id: 'review.list.load.more',
              defaultMessage: 'Load more Reviews',
            })}
          </Button>
        </div>
      )}
    </div>
  );
};
